import React, { useState } from "react";
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Text,
  Stack,
  Spinner,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  TextField,
} from "@fluentui/react";
import { Customer } from "@/api/customers";

interface Props {
  customer: Customer | null;
  isOpen: boolean;
  onDismiss: () => void;
  onSync: (
    customer: Customer,
    businessLineId: number,
    databaseName: string,
    entityName: string
  ) => Promise<void>;
}

const SyncCustomerDialog: React.FC<Props> = ({
  customer,
  isOpen,
  onDismiss,
  onSync,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessLineId, setBusinessLineId] = useState<number | null>(1);
  const [databaseName, setDatabaseName] = useState<string>("");
  const [entityName, setEntityName] = useState<string>("");

  const businessLineOptions: IDropdownOption[] = [
    { key: 1, text: "Truckload Dispatch" },
    { key: 2, text: "Truckload Brokerage" },
  ];

  React.useEffect(() => {
    if (customer) {
      setDatabaseName(customer.Name || "");
      setEntityName(customer.Description || "");
    }
  }, [customer]);

  const handleSync = async () => {
    if (!customer) return;

    if (businessLineId === null) {
      setError("Please select a Business Line");
      return;
    }

    if (!databaseName.trim()) {
      setError("Please enter a Database Name");
      return;
    }

    if (!entityName.trim()) {
      setError("Please enter an Entity Name");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSync(
        customer,
        businessLineId,
        databaseName.trim(),
        entityName.trim()
      );
      onDismiss();
    } catch (err: any) {
      setError(err?.message || "Failed to sync customer with FrostByte");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    if (!isLoading) {
      setError(null);
      setBusinessLineId(1);
      setDatabaseName("");
      setEntityName("");
      onDismiss();
    }
  };

  if (!customer) return null;

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: customer?.EntityId
          ? "Update Customer Sync with FrostByte"
          : "Sync Customer with FrostByte",
        subText: customer?.EntityId
          ? "This will update the existing entity in FrostByte for this customer."
          : "This will create a new entity in FrostByte for this customer.",
      }}
      modalProps={{
        isBlocking: true,
        isDarkOverlay: true,
      }}
      minWidth={500}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        {error && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline
            onDismiss={() => setError(null)}
          >
            {error}
          </MessageBar>
        )}

        <Stack tokens={{ childrenGap: 8 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            Customer Information
          </Text>

          <Stack horizontal tokens={{ childrenGap: 16 }}>
            <Stack
              tokens={{ childrenGap: 4 }}
              styles={{ root: { minWidth: 100 } }}
            >
              <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                External ID:
              </Text>
              <Text variant="small">{customer.Id}</Text>
            </Stack>

            {customer.CustomerNumber && (
              <Stack
                tokens={{ childrenGap: 4 }}
                styles={{ root: { minWidth: 120 } }}
              >
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                  Entity Number:
                </Text>
                <Text variant="small">{customer.CustomerNumber}</Text>
              </Stack>
            )}

            {customer.DatabaseIdentifier && (
              <Stack tokens={{ childrenGap: 4 }} styles={{ root: { flex: 1 } }}>
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                  Entity Database Letter:
                </Text>
                <Text variant="small">{customer.DatabaseIdentifier}</Text>
              </Stack>
            )}
          </Stack>

          {customer.Environment && (
            <Stack tokens={{ childrenGap: 4 }}>
              <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                Environment:
              </Text>
              <Text variant="small">{customer.Environment}</Text>
            </Stack>
          )}

          <TextField
            label="Database Name:"
            value={databaseName}
            onChange={(event, newValue) => {
              setDatabaseName(newValue || "");
              setError(null);
            }}
            disabled={isLoading}
            required
            styles={{
              fieldGroup: { width: "100%" },
            }}
          />

          <TextField
            label="Entity Name:"
            value={entityName}
            onChange={(event, newValue) => {
              setEntityName(newValue || "");
              setError(null);
            }}
            disabled={isLoading}
            required
            styles={{
              fieldGroup: { width: "100%" },
            }}
          />
        </Stack>

        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
            Business Line:
          </Text>
          <Dropdown
            placeholder="Select a business line"
            options={businessLineOptions}
            selectedKey={businessLineId}
            onChange={(event, option) => {
              if (option) {
                setBusinessLineId(option.key as number);
                setError(null);
              }
            }}
            disabled={isLoading}
            styles={{
              dropdown: { width: "100%" },
            }}
          />
        </Stack>

        <Text variant="medium">
          {customer?.EntityId
            ? "Do you want to update this customer's sync with FrostByte?"
            : "Do you want to sync this customer with FrostByte?"}
        </Text>
      </Stack>

      <DialogFooter>
        <PrimaryButton
          onClick={handleSync}
          disabled={isLoading}
          text={
            isLoading
              ? undefined
              : customer?.EntityId
              ? "Update FrostByte Sync"
              : "Sync with FrostByte"
          }
        >
          {isLoading && <Spinner size={1} />}
          {isLoading && (
            <span style={{ marginLeft: 8 }}>
              {customer?.EntityId ? "Updating..." : "Syncing..."}
            </span>
          )}
        </PrimaryButton>
        <DefaultButton
          onClick={handleDismiss}
          text="Cancel"
          disabled={isLoading}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default SyncCustomerDialog;
